import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface SpaceListItem {
  id: number
  name: string
  image_url: string | null
  location: string | null
  min_capacity: number
  max_capacity: number
  is_reservable: number
  is_visible: number
}

const LIMIT = 20

export default function SpaceListTab() {
  const navigate = useNavigate()
  const [spaces, setSpaces] = useState<SpaceListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isReservableFilter, setIsReservableFilter] = useState('')
  const [isVisibleFilter, setIsVisibleFilter] = useState('')
  const [skip, setSkip] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const fetchSpaces = useCallback(async (reservable: string, visible: string, skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (reservable !== '') params.append('is_reservable', reservable)
      if (visible !== '') params.append('is_visible', visible)
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setSpaces(data.spaces)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSpaces(isReservableFilter, isVisibleFilter, skip)
  }, [isReservableFilter, isVisibleFilter, skip, fetchSpaces])

  const handleDelete = async (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation()
    if (!window.confirm(`"${name}" 공간을 삭제하시겠습니까?\n신청 내역이 있는 경우 삭제되지 않습니다.`)) return
    setDeletingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/space/admin/${id}`), { method: 'DELETE' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '삭제 실패') }
      fetchSpaces(isReservableFilter, isVisibleFilter, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '삭제에 실패했습니다.')
    } finally {
      setDeletingId(null)
    }
  }

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div>
      {/* Filter + 등록 버튼 */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">예약 가능 여부</label>
            <select
              value={isReservableFilter}
              onChange={e => { setIsReservableFilter(e.target.value); setSkip(0) }}
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[150px] bg-white"
            >
              <option value="">전체</option>
              <option value="1">예약 가능</option>
              <option value="0">예약 불가</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">노출 여부</label>
            <select
              value={isVisibleFilter}
              onChange={e => { setIsVisibleFilter(e.target.value); setSkip(0) }}
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[130px] bg-white"
            >
              <option value="">전체</option>
              <option value="1">노출</option>
              <option value="0">숨김</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[13px] text-gray-400">
            총 <span className="text-[#887267] font-bold">{total}</span>개
          </span>
          <button
            onClick={() => navigate('/admin/space/create')}
            className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-[#6b5248] transition-colors border-none"
          >
            + 공간 등록
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
        {loading ? (
          <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
            불러오는 중...
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[48px]">ID</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">공간명</th>
                <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[150px]">위치</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">이용 인원</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">예약</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">노출</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]"></th>
              </tr>
            </thead>
            <tbody>
              {spaces.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-300 text-[13px]">
                    등록된 공간이 없습니다.
                  </td>
                </tr>
              ) : spaces.map(s => (
                <tr
                  key={s.id}
                  className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/space/${s.id}`)}
                >
                  <td className="px-4 py-3 text-[13px] text-gray-400">{s.id}</td>
                  <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a] whitespace-nowrap">{s.name}</td>
                  <td className="hidden sm:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{s.location ?? '-'}</td>
                  <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{s.min_capacity}~{s.max_capacity}명</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                      s.is_reservable ? 'bg-[#e8f5e9] text-[#388e3c]' : 'bg-[#f5f5f5] text-[#9e9e9e]'
                    }`}>
                      {s.is_reservable ? '가능' : '불가'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                      s.is_visible ? 'bg-[#e3f2fd] text-[#1976d2]' : 'bg-[#f5f5f5] text-[#9e9e9e]'
                    }`}>
                      {s.is_visible ? '노출' : '숨김'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => navigate(`/admin/space/${s.id}`)}
                        className="text-[12px] text-[#887267] hover:text-[#6b5248] border border-[#e8d8ce] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors"
                      >수정</button>
                      <button
                        onClick={e => handleDelete(e, s.id, s.name)}
                        disabled={deletingId === s.id}
                        className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                      >삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-5">
          <button
            onClick={() => setSkip((currentPage - 2) * LIMIT)}
            disabled={currentPage === 1}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
            .reduce<(number | '...')[]>((acc, p, idx, arr) => {
              if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                acc.push('...')
              acc.push(p)
              return acc
            }, [])
            .map((p, idx) =>
              p === '...'
                ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                : <button
                    key={p}
                    onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                      currentPage === p
                        ? 'bg-[#887267] text-white border-[#887267]'
                        : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                    }`}
                  >{p}</button>
            )}
          <button
            onClick={() => setSkip(currentPage * LIMIT)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
          >›</button>
        </div>
      )}
    </div>
  )
}